import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import type { SignUpRequest } from '@hero/api';
import { useSignUpWizardManager, type WizardPersona } from '@hero/web-hooks';
import { CompanyStep, type CompanyStepValues } from './CompanyStep';
import { DriverStep, type DriverStepValues } from './DriverStep';
import {
  IndividualCustomerStep,
  type IndividualCustomerStepValues,
} from './IndividualCustomerStep';
import { PersonaStep } from './PersonaStep';
import { StepProgress } from './StepProgress';

export interface SignUpWizardProps {
  /** Optional override for post-success navigation. Defaults to the
   * shared `auth_verified` route inside `useSignUpWizardManager`. */
  onSuccess?: () => void;
  className?: string;
}

const stepMotion = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.22, ease: 'easeOut' },
} as const;

/**
 * Unified signup wizard — drives the persona → step-2 flow and submits
 * via the shared `auth.signUp` endpoint. Mountable by any web app
 * (currently `carrier-dashboard`, the slated single-dashboard at
 * `app.tryhero.io`).
 *
 * Architecture (per `.cursorrules` "No Redux"):
 * - Persona / step / persistence handled by `useSignUpWizardManager`
 *   (`@hero/web-hooks`). It also persists the resulting tokens via
 *   `tokens.ts` and routes to `auth_verified` on success.
 * - Each step-2 component owns its own React Hook Form instance.
 *   On submit, the wizard reshapes the step-local values into the
 *   discriminated `SignUpRequest` and calls `manager.submit(...)`.
 * - No Redux, no global form store. Cross-step persona / step state
 *   is the only shared piece, and it lives in the manager.
 *
 * UX details:
 * - Persona selection requires an explicit "Continue" tap (see
 *   `PersonaStep`) — tap-to-advance was jarring.
 * - All step transitions are wrapped in `AnimatePresence` so going
 *   forward / backward feels continuous.
 */
export function SignUpWizard({ onSuccess, className }: SignUpWizardProps) {
  const { t } = useTranslation('m-auth');
  const { persona, step, setPersona, setStep, back, submit, isPending } = useSignUpWizardManager({
    onSuccess,
  });

  // The persona pick is staged locally so the user can change their
  // mind without immediately advancing. Confirmed via the Continue
  // button in `PersonaStep`.
  const [pendingPersona, setPendingPersona] = useState<WizardPersona | null>(persona);

  const totalSteps = 2;
  const currentStep = step === 'persona' ? 1 : 2;

  const handlePersonaContinue = () => {
    if (!pendingPersona) return;
    setPersona(pendingPersona);
    if (pendingPersona === 'individual_customer') setStep('profile');
    else if (pendingPersona === 'company') setStep('company');
    else setStep('driver');
  };

  const handleCompanySubmit = async (values: CompanyStepValues) => {
    const payload: SignUpRequest = {
      personType: 'company',
      name: values.name,
      company: {
        roles: values.roles,
        companyName: values.companyName,
        ...(values.vehicleTypes.length > 0 ? { vehicleTypes: values.vehicleTypes } : {}),
        ...(typeof values.totalVehicleCount === 'number'
          ? { totalVehicleCount: values.totalVehicleCount }
          : {}),
        ...(typeof values.monthlyExpectedOrder === 'number'
          ? { monthlyExpectedOrder: values.monthlyExpectedOrder }
          : {}),
      },
    };
    await submit(payload);
  };

  const handleDriverSubmit = async (values: DriverStepValues) => {
    // The driver picks a single vehicle type; the unified
    // `signUpDriverSchema.vehicleTypes` is an array, so we wrap it.
    // No `totalVehicleCount` for drivers — fleet totals are an Org
    // concern and a driver-of-company outcome inherits the implicit
    // count of 1 on the legacy Carrier mirror downstream.
    const vehicleTypes = values.vehicleType ? [values.vehicleType] : [];
    const payload: SignUpRequest = {
      personType: 'driver',
      name: values.name,
      driverEmployment: values.driverEmployment,
      vehicleTypes,
      ...(values.driverEmployment === 'company' && values.companyName
        ? {
            // Driver-also-creates-company case: default the new Org
            // to a `carrier_company` since that's the natural role for
            // the org a driver works under (the user can flip / add
            // roles later from the org settings).
            company: {
              roles: ['carrier_company'],
              companyName: values.companyName,
              vehicleTypes,
            },
          }
        : {}),
    };
    await submit(payload);
  };

  const handleIndividualCustomerSubmit = async (values: IndividualCustomerStepValues) => {
    const payload: SignUpRequest = {
      personType: 'individual_customer',
      name: values.name,
    };
    await submit(payload);
  };

  return (
    <div className={className}>
      <div className="mb-6">
        <h1 className="text-center text-2xl font-semibold text-gray-900">
          {t('wizard.title')}
        </h1>
        <p className="mt-2 text-center text-sm text-gray-500">{t('wizard.subtitle')}</p>
      </div>

      <StepProgress current={currentStep} total={totalSteps} className="mb-6" />

      <AnimatePresence mode="wait" initial={false}>
        {step === 'persona' ? (
          <motion.div key="persona" {...stepMotion}>
            <PersonaStep
              value={pendingPersona}
              onChange={setPendingPersona}
              onContinue={handlePersonaContinue}
            />
          </motion.div>
        ) : null}

        {step === 'company' ? (
          <motion.div key="company" {...stepMotion}>
            <CompanyStep
              onSubmit={handleCompanySubmit}
              onBack={back}
              isPending={isPending}
            />
          </motion.div>
        ) : null}

        {step === 'driver' ? (
          <motion.div key="driver" {...stepMotion}>
            <DriverStep
              onSubmit={handleDriverSubmit}
              onBack={back}
              isPending={isPending}
            />
          </motion.div>
        ) : null}

        {step === 'profile' && persona === 'individual_customer' ? (
          <motion.div key="individual_customer" {...stepMotion}>
            <IndividualCustomerStep
              onSubmit={handleIndividualCustomerSubmit}
              onBack={back}
              isPending={isPending}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
